import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { featuredVideos, categories } from '@/mocks/homeData';

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  useEffect(() => {
    const timer = setInterval(() => {
      goToSlide((currentSlide + 1) % featuredVideos.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [currentSlide, goToSlide]);

  const currentVideo = featuredVideos[currentSlide];

  return (
    <section className="relative w-full h-[520px] md:h-[640px] overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center transition-all duration-700 ease-in-out"
        style={{ backgroundImage: `url(${currentVideo.imageUrl})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
      </div>

      <div className="relative z-10 h-full w-full flex items-center">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="max-w-2xl">
            <span className="inline-block bg-primary-500/90 text-background-50 text-xs font-medium px-3 py-1 rounded-full mb-4">
              {currentVideo.categoryName} · {currentVideo.year}
            </span>
            <strong className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4 leading-tight block">
              {currentVideo.title}
            </strong>
            <p className="text-background-200/90 text-sm md:text-base leading-relaxed mb-6 line-clamp-2">
              {currentVideo.description}
            </p>
            <div className="flex items-center gap-4 mb-8">
              <span className="flex items-center gap-1 text-accent-400 text-sm">
                <i className="ri-star-fill"></i> {currentVideo.rating}
              </span>
              <span className="text-background-300 text-sm">{currentVideo.views} 次播放</span>
              <span className="text-background-300 text-sm">{currentVideo.duration}</span>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to={`/category/${currentVideo.category}`}
                className="bg-primary-500 hover:bg-primary-600 text-background-50 px-7 py-3 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
              >
                <i className="ri-play-fill text-lg"></i> 立即观看
              </Link>
              <button className="bg-background-50/15 hover:bg-background-50/25 text-background-50 px-7 py-3 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2 backdrop-blur-sm">
                <i className="ri-add-line text-lg"></i> 加入收藏
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        {featuredVideos.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              index === currentSlide
                ? 'w-7 h-2 bg-primary-500'
                : 'w-2 h-2 bg-background-50/40 hover:bg-background-50/70'
            }`}
          />
        ))}
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pb-2">
          <div className="flex items-center gap-3 overflow-x-auto pb-2">
            {categories.slice(0, 6).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-2 bg-background-50/15 backdrop-blur-sm hover:bg-background-50/25 text-background-50 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap border border-background-50/15"
              >
                <i className={`${cat.icon}`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}